import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

const defaultPlatformSettings = {
  platform_name: '',
  logo_url: null,
  primary_color: '#0052FF',
  secondary_color: '#FFFFFF',
  accent_color: '#0052FF',
  dark_mode_default: false,
  default_language: 'fr',
  legal_notice: '',
  privacy_policy: '',
  terms_of_service: ''
};

const defaultClientTemplate = {
  pin_enabled: true,
  pin_length: 6,
  pin_max_attempts: 3,
  session_timeout_minutes: 30,
  // Menu visibility - Main categories
  menu_dashboard: true,
  menu_news: true,
  menu_investments: true,
  menu_my_investments: true,
  menu_simulator: true,
  menu_simulation: true,
  menu_trade: true,
  menu_stock: true,
  menu_crypto: true,
  menu_wallets: true,
  menu_transactions: true,
  menu_messages: true,
  menu_documents: true,
  menu_profile: true,
  // Sub-menu visibility - BOURSE
  submenu_bourse_salle: true,
  submenu_bourse_epargnes: true,
  submenu_bourse_portefeuille: true,
  // Sub-menu visibility - CRYPTO
  submenu_crypto_salle: true,
  submenu_crypto_epargnes: true,
  // Widgets
  widget_wallets: true,
  widget_portfolios: true,
  widget_balances: true,
  widget_gains: true,
  widget_progress: true,
  widget_analyst_card: true,
  widget_testimonials: true,
  // Trading
  trading_manual_allowed: false,
  trading_auto_allowed: false,
  trading_max_leverage: 10,
  trading_max_exposure: 50.0,
  trading_default_state: 'neutral',
  // KYC
  kyc_required_documents: true,
  kyc_required_proof: true,
  kyc_required_address: true,
  kyc_block_withdrawals: true,
  // Currency
  default_currency: 'EUR',
  multi_currency: false
};

export const SettingsProvider = ({ children }) => {
  const [platformSettings, setPlatformSettings] = useState(defaultPlatformSettings);
  const [clientTemplate, setClientTemplate] = useState(defaultClientTemplate);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Theme application is now handled by ThemeContext
  // This context is kept for backward compatibility with clientTemplate

  const fetchSettings = async () => {
    try {
      const [platformRes, templateRes] = await Promise.all([
        axios.get(`${API}/settings/platform`, { timeout: 10000 }),
        axios.get(`${API}/settings/template/public`, { timeout: 10000 })
      ]);
      
      setPlatformSettings({ ...defaultPlatformSettings, ...platformRes.data });
      setClientTemplate({ ...defaultClientTemplate, ...templateRes.data });
      setDarkMode(platformRes.data.dark_mode_default ?? false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePlatformSettings = async (settings, token) => {
    const response = await axios.put(`${API}/settings/platform`, settings, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setPlatformSettings(prev => ({ ...prev, ...settings }));
    return response.data;
  };

  const updateClientTemplate = async (template, token) => {
    const response = await axios.put(`${API}/settings/template`, template, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setClientTemplate(prev => ({ ...prev, ...template }));
    return response.data;
  };

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const value = {
    platformSettings,
    clientTemplate,
    darkMode,
    loading,
    updatePlatformSettings,
    updateClientTemplate,
    toggleDarkMode,
    refreshSettings: fetchSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// Helper function to convert hex to HSL
function hexToHsl(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '43 74% 52%';
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
